import { useQuery } from '@tanstack/react-query';
import {
  EmptyState,
  ErrorNotice,
  LoadingState,
  Panel,
  StatTile,
  TrendChart,
  type TrendOverlay,
  type TrendPoint,
  type TrendSeries,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { LIVE_REFRESH_MS } from '@/lib/report-window';
import { PACING_COMPARISON, readLeadPacing, type LeadPacing, type LeadPacingComparison } from '@/lib/leads';

/** "October 2026". */
export function monthName(month: string): string {
  return new Date(`${month}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** "October". */
function shortMonthName(month: string): string {
  return new Date(`${month}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
}

/** "Oct 9". */
function dayLabel(day: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Chart overlay slot per comparison: last month solid, last year dashed. */
const OVERLAY_TONE: Readonly<Record<LeadPacingComparison['key'], 0 | 1>> = {
  [PACING_COMPARISON.lastMonth]: 0,
  [PACING_COMPARISON.lastYear]: 1,
};

/**
 * The month as a running total by business day. Bars are this month, so days
 * that have not started stay empty while the comparison lines run on to the
 * end of their months.
 */
export function toPacingChart(pacing: LeadPacing): { points: TrendPoint[]; series: TrendSeries[]; overlays: TrendOverlay[] } {
  const points: TrendPoint[] = pacing.days.map((day) => ({
    key: String(day.index),
    label: day.day ? dayLabel(day.day) : `Day ${day.index}`,
    value: day.current ?? 0,
    details: [day.current === null ? 'Not reached yet this month' : `Business day ${day.index} of ${pacing.businessDays}`],
  }));
  const overlays: TrendOverlay[] = pacing.comparisons.map((comparison) => ({
    key: comparison.key,
    label: monthName(comparison.month),
    values: pacing.days.map((day) => (comparison.key === PACING_COMPARISON.lastMonth ? day.lastMonth : day.lastYear)),
    tone: OVERLAY_TONE[comparison.key],
  }));
  // One named series, so the legend reads the month and not the unit.
  const series: TrendSeries[] = [{ key: pacing.month, label: monthName(pacing.month), values: points.map((point) => point.value) }];
  return { points, series, overlays };
}

/** "64 now against 35 by business day 7 · finished at 189". */
function comparisonHint(pacing: LeadPacing, comparison: LeadPacingComparison): string {
  return `${pacing.atCompleted.toLocaleString()} now against ${comparison.atSamePoint.toLocaleString()} by business day ${
    pacing.completedBusinessDays
  } · finished at ${comparison.total.toLocaleString()}`;
}

/**
 * How the current month is pacing: counted leads so far against last month
 * and the same month a year ago at the same business day, with the total the
 * month is heading for. Always the current month, whatever range the page shows.
 *
 * @returns The month pace panel.
 */
export function LeadPacingPanel(): React.ReactElement {
  const pacing = useQuery({
    queryKey: ['lead-pacing'],
    queryFn: ({ signal }) => readLeadPacing(signal),
    refetchInterval: LIVE_REFRESH_MS,
  });

  if (pacing.isError) {
    return (
      <Panel className="mt-4 p-5">
        <ErrorNotice title="Month pace unavailable" message={errorMessage(pacing.error)} />
      </Panel>
    );
  }
  if (pacing.isPending) {
    return (
      <Panel className="mt-4 p-5">
        <LoadingState label="Working out this month’s pace…" />
      </Panel>
    );
  }

  const data = pacing.data;
  const thisMonth = shortMonthName(data.month);
  const started = data.completedBusinessDays > 0;
  if (data.monthToDate === 0 && data.comparisons.every((comparison) => comparison.total === 0)) {
    return (
      <Panel className="mt-4 p-5">
        <EmptyState title="No month pace yet" message="The pace fills in as the sync brings leads in." />
      </Panel>
    );
  }
  const { points, series, overlays } = toPacingChart(data);

  return (
    <Panel className="mt-4 p-5">
      <div className="mb-3">
        <h2 className="text-portal-card-title font-bold text-portal-ink">{monthName(data.month)} pace</h2>
        <p className="text-portal-micro text-portal-muted">
          Counted leads, compared by business day so months with different calendars line up. Comparisons stop at the
          last full business day ({data.completedBusinessDays} of {data.businessDays}); today is still filling in.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <StatTile
          label={`${thisMonth} so far`}
          value={data.monthToDate.toLocaleString()}
          hint={
            data.perBusinessDay === null
              ? 'The first business day is still in progress'
              : `${data.perBusinessDay.toLocaleString()} a business day over ${data.completedBusinessDays} full days`
          }
        />
        {data.comparisons.map((comparison) => (
          <StatTile
            key={comparison.key}
            label={`Against ${monthName(comparison.month)}`}
            value={
              !started || comparison.changePct === null
                ? '—'
                : `${comparison.changePct > 0 ? '+' : ''}${comparison.changePct.toLocaleString()}%`
            }
            hint={
              started
                ? comparisonHint(data, comparison)
                : `Finished at ${comparison.total.toLocaleString()} · compares once a business day is over`
            }
          />
        ))}
        <StatTile
          label="On pace for"
          value={data.projected === null ? '—' : data.projected.toLocaleString()}
          hint={
            data.projected === null
              ? 'Projects once a business day is over'
              : `${thisMonth} total if the daily rate holds for all ${data.businessDays} business days`
          }
        />
      </div>
      <div className="mt-4">
        <TrendChart
          points={points}
          series={series}
          overlays={overlays}
          variant="bar"
          title={`${monthName(data.month)}, running total`}
          unit="leads"
        />
      </div>
    </Panel>
  );
}
